import { useNavigate } from "@tanstack/react-router"
import { EllipsisVertical, LogOut, Moon, Sun, UserRound } from "lucide-react"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Tooltip } from "@/components/ui/tooltip"
import { type Scope, tokenLabel } from "@/lib/connection"
import { useT } from "@/lib/i18n"
import { useDefinitionsLive } from "@/lib/live"
import { useSession } from "@/lib/session"
import { applyTheme, useTheme } from "@/lib/theme"
import { cn } from "@/lib/utils"
import { usePrincipal } from "@/widget/runtime"
import { LocaleSwitcher } from "./locale-switcher"

function host(baseUrl: string): string {
  try {
    return new URL(baseUrl).host
  } catch {
    return baseUrl
  }
}

/** Console's user footer, backed by the cell-resolved principal and Norn session. */
export function NavUser({ scope, collapsed = false }: { scope: Scope; collapsed?: boolean }) {
  const { connection, disconnect, auth } = useSession()
  const principal = usePrincipal(scope)
  const navigate = useNavigate()
  const t = useT()
  const theme = useTheme()
  const signedIn = auth.kind === "oidc"
  const profile = auth.kind === "oidc" ? (auth.profile?.name ?? auth.profile?.email) : null
  const identity =
    principal.status === "ready"
      ? principal.actor
      : signedIn
        ? t("shell.signedIn")
        : tokenLabel(connection?.token ?? null)
  const name = profile && principal.status === "ready" ? `${profile} · ${identity}` : (profile ?? identity)
  const cell = connection ? host(connection.baseUrl) : t("shell.notConnected")
  const action =
    "flex size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground [&_svg]:size-4"
  return (
    <div className="space-y-2 p-2">
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={t("shell.userMenu")}
            className={cn(
              "flex h-12 w-full items-center gap-2 rounded-lg px-2 text-start hover:bg-accent",
              collapsed && "justify-center px-0",
            )}
          >
            <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
              <UserRound className="size-4" />
            </span>
            {!collapsed ? (
              <>
                <span className="grid min-w-0 flex-1 text-sm leading-tight">
                  <span className="truncate font-medium">{name}</span>
                  <span className="truncate text-xs text-muted-foreground">{cell}</span>
                </span>
                <EllipsisVertical className="size-4 shrink-0 text-muted-foreground" />
              </>
            ) : null}
          </button>
        </PopoverTrigger>
        <PopoverContent side="top" align="start" className="w-64 space-y-1 p-3">
          <p className="truncate text-sm font-medium">{name}</p>
          <p className="break-all text-xs text-muted-foreground">{identity}</p>
          <p className="break-all text-xs text-muted-foreground">{connection?.baseUrl}</p>
          <LiveIndicator />
          {!signedIn && connection?.actor ? (
            <p className="break-all text-xs text-muted-foreground">{t("shell.actor", { actor: connection.actor })}</p>
          ) : null}
        </PopoverContent>
      </Popover>
      {!collapsed ? (
        <div className="px-2">
          <LiveIndicator />
        </div>
      ) : null}
      <div className={cn("flex items-center gap-1 px-1", collapsed && "flex-col px-0")}>
        <LocaleSwitcher className={action} />
        <Tooltip content={theme === "dark" ? t("shell.themeLight") : t("shell.themeDark")}>
          <button
            type="button"
            onClick={() => applyTheme(theme === "dark" ? "light" : "dark")}
            aria-label={t("shell.toggleTheme")}
            className={action}
          >
            {theme === "dark" ? <Sun /> : <Moon />}
          </button>
        </Tooltip>
        <Tooltip content={signedIn ? t("shell.signOut") : t("shell.disconnect")}>
          <button
            type="button"
            aria-label={signedIn ? t("shell.signOut") : t("shell.disconnect")}
            onClick={() => {
              disconnect()
              void navigate({ to: "/connect" })
            }}
            className={cn(action, !collapsed && "ms-auto")}
          >
            <LogOut className="rtl-mirror" />
          </button>
        </Tooltip>
      </div>
    </div>
  )
}

/** Whether definitions on screen follow the cell's change stream (#3223). */
function LiveIndicator() {
  const t = useT()
  const { status } = useDefinitionsLive()
  const label =
    status.kind === "live"
      ? t("live.definitionsLive", { revision: status.revision })
      : status.kind === "connecting"
        ? t("live.connecting")
        : t("live.notLive")
  return (
    <p
      className="flex min-w-0 items-center gap-1.5 text-2xs text-subtle-foreground"
      title={status.kind === "stopped" ? (status.reasonKey ? t(status.reasonKey) : status.reason) : label}
    >
      <span
        aria-hidden
        className={cn(
          "size-1.5 shrink-0 rounded-full",
          status.kind === "live" ? "bg-success" : status.kind === "connecting" ? "bg-subtle-foreground" : "bg-warning",
        )}
      />
      <span className="min-w-0 truncate">{label}</span>
    </p>
  )
}
